import { object, string, boolean, number, ref } from 'yup'

const crossSectionSchema = () =>
  object({
    shape: object({
      height: number().default(0).moreThan(0),
      width: number().default(0).moreThan(0),
      kind_literal: string().default('RectangularCS'),
    }),
    unique_name: string().default(''),
    material: object().default(null).nullable().required(),
    processed: boolean().default(true),
    producer: string().default('').nullable(),
    description: string().default('').nullable(),
    usage_class: string().default('One'),
    kind: string().default('Timber'),
  })

const insulationValueSchema = () =>
  object({
    name: string().required('Insulation name is required when insulation is enabled'),
    producer: string().nullable(),
    density: number()
      .required('Insulation density is required when insulation is enabled')
      .moreThan(0),
    description: string().nullable(),
  })

const createLayerSchema = ({
  kind,
  uniqueName,
  standardCrossSectionKey,
  additionalCrossSectionKeys = [],
}: {
  kind: string
  uniqueName: string
  standardCrossSectionKey: string
  additionalCrossSectionKeys?: string[]
}) => {
  const standardCrossSectionName = standardCrossSectionKey
  const crossSectionEntries = [standardCrossSectionKey, ...additionalCrossSectionKeys].reduce(
    (acc, key) => ({ ...acc, [key]: crossSectionSchema() }),
    {},
  )

  return object({
    unique_name: string().default(uniqueName),
    kind: string().default(kind),
    ...crossSectionEntries,
    rip_step_size: number()
      .default(0.625)
      .moreThan(0)
      .test(
        'rip-step-size-greater-than-width',
        'Muss größer oder gleich Breite des Standardquerschnitts sein.',
        function (value) {
          if (!value) return true

          const standardCrossSection = this.parent?.[standardCrossSectionName]

          const width = standardCrossSection?.shape?.width

          return value >= width
        },
      ),
    hasInsulation: boolean().default(false),
    insulation_thickness: number()
      .nullable()
      .default(null)
      .when('hasInsulation', {
        is: true,
        then: number()
          .required('Insulation thickness is required when insulation is enabled')
          .moreThan(0)
          .max(
            ref(`${standardCrossSectionName}.shape.height`),
            'Muss kleiner oder gleich Querschnittshöhe sein',
          ),
        otherwise: number().nullable().default(null),
      }),
    insulation: object()
      .nullable()
      .default(null)
      .when('hasInsulation', {
        is: true,
        then: insulationValueSchema(),
        otherwise: object().nullable().default(null),
      }),
  })
}

export const getTimberFrameLayerSchema = (variant: TimberFrameLayerVariant) => {
  if (variant === 'roof-ceiling') {
    return object({
      materialTypeStandardBeam: string().default('softwoodMaterial'),
      layer: createLayerSchema({
        kind: 'timber-frame-layer-roof-ceiling',
        uniqueName: 'timber-frame-layer-roof-ceiling',
        standardCrossSectionKey: 'crossSectionStandardBeam',
        additionalCrossSectionKeys: [],
      }),
    })
  }

  return object({
    materialTypeStandardRip: string().default('softwoodMaterial'),
    materialTypeLintel: string().default('softwoodMaterial'),
    materialTypeTopChord: string().default('softwoodMaterial'),
    materialTypeBottomChord: string().default('softwoodMaterial'),
    layer: createLayerSchema({
      kind: 'timber-frame-layer-wall',
      uniqueName: 'timber-frame-layer-wall',
      standardCrossSectionKey: 'crossSectionStandardRip',
      additionalCrossSectionKeys: [
        'crossSectionLintel',
        'crossSectionTopChord',
        'crossSectionBottomChord',
      ],
    }),
  })
}
